import type { InputHTMLAttributes, ReactNode, Ref } from 'react';
import { useEffect, useRef } from 'react';
import type { CheckboxSize } from './Checkbox.css';
import {
  root,
  rootDisabled,
  controlWrapper,
  nativeInput,
  box,
  checkMark,
  dashMark,
  labelText,
} from './Checkbox.css';

export type { CheckboxSize };

export interface CheckboxProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type' | 'size'
> {
  /** Ref to the underlying input */
  ref?: Ref<HTMLInputElement>;
  /** Visual size */
  checkboxSize?: CheckboxSize;
  /** Mixed (tri-state) visual — sets the input's `indeterminate` DOM property */
  indeterminate?: boolean;
  /** Visible label rendered next to the box. Provide this or `aria-label` for accessibility. */
  label?: ReactNode;
  /** Custom class name applied to the root label */
  className?: string;
}

export function Checkbox({
  checkboxSize = 'md',
  indeterminate = false,
  disabled = false,
  label,
  className,
  ref,
  ...props
}: CheckboxProps) {
  const innerRef = useRef<HTMLInputElement>(null);

  // `indeterminate` is a DOM property, not an attribute — sync imperatively.
  useEffect(() => {
    if (innerRef.current) innerRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  const setRef = (node: HTMLInputElement | null) => {
    innerRef.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) (ref as { current: HTMLInputElement | null }).current = node;
  };

  return (
    <label
      className={`${root}${disabled ? ` ${rootDisabled}` : ''}${className ? ` ${className}` : ''}`}
    >
      <span className={controlWrapper}>
        <input
          ref={setRef}
          type="checkbox"
          disabled={disabled}
          className={nativeInput}
          {...props}
        />
        <span className={box[checkboxSize]} aria-hidden="true">
          <svg
            className={checkMark}
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            focusable="false"
          >
            <path d="M3.5 8.5l3 3 6-7" />
          </svg>
          <span className={dashMark} />
        </span>
      </span>
      {label != null && <span className={labelText[checkboxSize]}>{label}</span>}
    </label>
  );
}
